import * as THREE from "three";
import { dirty } from "./hpBars";

/**
 * Unit ground rings (art review units F6): the contact shadow under every unit plus a thin team-coloured rim, all of
 * one team in ONE draw.
 *
 * This replaces the per-unit blob (a 0.22 black disc at 0.35 opacity, one draw each, art/SHADING.md §4). The shadow
 * keeps the blob's curve (full at the centre, 55 % at 0.6 of its radius, gone at the edge) inside 80 % of the quad; the
 * rim sits at 86 %: enemies #d8402e at 0.7, troops #3fa9ff — Kingshot's trick for telling the sides apart in a melee
 * where both are steel grey. The rim colour is per instance, so the views tint it with status (ice when slowed, green
 * pulse when poisoned). Bosses get a 0.9-wide rune ring instead: glyph cells turning at 0.3 rad/s with an additive
 * 1.5 Hz pulse (brighter with the boss's rim heat), and attack telegraphs use the same draw (`telegraph`).
 *
 * Shadow (darkening) and rune glow (adding) share one draw through premultiplied-alpha blending: the fragment writes
 * (colour × coverage + glow, coverage), blended ONE / ONE_MINUS_SRC_ALPHA, so a pixel of pure shadow is (0, a) and a
 * pixel of pure glow is (glow, 0).
 */
export const RING = {
  enemy: 0xd8402e, troop: 0x3fa9ff, rimAlpha: 0.7, shadowAlpha: 0.35,
  /**
   * The troops' team disc (M30 #23, "flag troops blend into the terrain"): a bolder rim, a soft glow of the team
   * colour inside it and a wider ring, so a blue-steel soldier on green, ash or snow still reads as OURS at game zoom.
   */
  troopRim: 0x2f9bff, troopRimAlpha: 0.95, troopFill: 0.4, troopWidth: 1.3,
  /** Boss rune ring: minimum width, turn rate (rad/s) and pulse rate (Hz). */
  bossWidth: 0.9, bossSpin: 0.3, bossPulseHz: 1.5,
} as const;

const VERT = /* glsl */ `
attribute vec4 aPos;
attribute vec4 aCol;
attribute vec4 aPrm;
varying vec2 vP;
varying vec4 vCol;
varying vec4 vPrm;
void main() {
  vP = position.xy * 2.0;
  vCol = aCol; vPrm = aPrm;
  vec3 w = aPos.xyz + vec3(position.x, 0.0, -position.y) * aPos.w;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(w, 1.0);
}`;

const FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vP;
varying vec4 vCol;
varying vec4 vPrm;
void main() {
  float r = length(vP);
  if (r > 1.0) discard;
  float rs = r / 0.8;
  float sh = rs < 0.6 ? mix(1.0, 0.55, rs / 0.6) : mix(0.55, 0.0, clamp((rs - 0.6) / 0.4, 0.0, 1.0));
  sh *= vPrm.x;
  float rimA = vCol.a * (1.0 - smoothstep(0.03, 0.065, abs(r - 0.86)));
  vec3 glow = vec3(0.0);
  if (vPrm.y > 3.5) {
    // light pool (kind 4): a soft additive glow of the colour on the ground, no rim, no shadow (a lantern's light)
    float q = 1.0 - r;
    glow = vCol.rgb * (vPrm.z * q * q);
    sh = 0.0; rimA = 0.0;
  } else if (vPrm.y > 2.5) {
    // team disc: a thicker rim and the team colour glowing softly inside it, over the contact shadow
    rimA = vCol.a * (1.0 - smoothstep(0.05, 0.095, abs(r - 0.86)));
    glow = vCol.rgb * (vPrm.z * (1.0 - smoothstep(0.35, 0.82, r)));
  } else if (vPrm.y > 1.5) {
    float p = vPrm.z;
    float front = 1.0 - smoothstep(0.0, 0.06, abs(r - p));
    float rim = 1.0 - smoothstep(0.0, 0.05, abs(r - 0.95));
    rimA = vCol.a * clamp(step(r, p) * (0.25 + 0.2 * p) + rim * 0.85 + front * 0.6, 0.0, 1.0);
    sh = 0.0;
    glow = vCol.rgb * (front * p * 1.3);
  } else if (vPrm.y > 0.5) {
    float turns = (atan(vP.y, vP.x) + uTime * ${RING.bossSpin.toFixed(2)}) / 6.2831853 * 18.0;
    float cell = floor(turns);
    float seg = fract(turns);
    float rr = (r - 0.74) / 0.22;
    float band = step(0.0, rr) * step(rr, 1.0);
    float glyph = step(0.18, seg) * step(seg, 0.82) * step(0.45, fract(rr * 2.0 + fract(sin(cell * 12.9898) * 43758.5453)));
    float lines = (1.0 - smoothstep(0.0, 0.02, abs(r - 0.72))) + (1.0 - smoothstep(0.0, 0.02, abs(r - 0.98)));
    float pulse = 0.6 + 0.4 * sin(uTime * ${(RING.bossPulseHz * 2 * Math.PI).toFixed(4)});
    glow = vCol.rgb * ((glyph * band * 0.9 + lines) * pulse * 1.4 * (1.0 + 2.0 * vPrm.z));
    rimA *= 0.35;
  }
  vec3 rimC = linearToOutputTexel(vec4(vCol.rgb, 1.0)).rgb;
  vec3 glowC = linearToOutputTexel(vec4(glow, 1.0)).rgb;
  float a = 1.0 - (1.0 - sh) * (1.0 - rimA);
  gl_FragColor = vec4((rimC * rimA + glowC) * vPrm.w, a * vPrm.w);
}`;

export class UnitRings {
  readonly mesh: THREE.Mesh;
  /** Seconds, for the boss rune ring; the owning view advances it. */
  readonly time = { value: 0 };
  private readonly geo: THREE.InstancedBufferGeometry;
  private pos!: THREE.InstancedBufferAttribute;
  private col!: THREE.InstancedBufferAttribute;
  private prm!: THREE.InstancedBufferAttribute;
  private capacity = 0;
  private n = 0;

  constructor(capacity = 64) {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.setAttribute("position", quad.getAttribute("position"));
    this.grow(capacity);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uTime: this.time }, vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthWrite: false, blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
    });
    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 1;                            // as the blobs were: over the ground, under the units' transparents
    this.mesh.name = "unit_rings";
  }

  begin(): void { this.n = 0; }

  /**
   * One ring on the ground at (x, y, z), `width` world units across. `rgb` is the rim colour (linear), `rim` its alpha,
   * `shadow` the contact shadow's alpha, `boss` switches to the rune ring, `fade` scales the whole thing (death).
   */
  push(x: number, y: number, z: number, width: number, rgb: THREE.Color, rim: number, shadow: number, boss: boolean, fade: number, heat = 0): void {
    this.add(x, y, z, width, rgb, rim, shadow, boss ? 1 : 0, heat, fade);
  }

  /**
   * A troop's team disc (kind 3): the ring with a thicker rim and `fill` of the team colour glowing inside it.
   */
  pushTeam(x: number, y: number, z: number, width: number, rgb: THREE.Color, rim: number, shadow: number, fill: number, fade: number): void {
    this.add(x, y, z, width, rgb, rim, shadow, 3, fill, fade);
  }

  /**
   * A pool of light on the ground (kind 4): `rgb` (linear) glowing additively with `strength`, falling off softly to
   * the edge of a disc `width` across; no rim and no shadow (a troop skin's lantern, render/skins/kit/troopFx.ts).
   */
  pushGlow(x: number, y: number, z: number, width: number, rgb: THREE.Color, strength: number, fade = 1): void {
    this.add(x, y, z, width, rgb, 0, 0, 4, strength, fade);
  }

  /**
   * An attack telegraph (boss F7): a disc on the ground that fills from the centre as `progress` runs 0 → 1, with a
   * bright leading edge and a fixed outer ring, so the player sees where and when the blow will land.
   */
  telegraph(x: number, y: number, z: number, width: number, rgb: THREE.Color, progress: number, fade = 1): void {
    this.add(x, y, z, width, rgb, 0.9, 0, 2, Math.max(0, Math.min(1, progress)), fade);
  }

  private add(x: number, y: number, z: number, width: number, rgb: THREE.Color, rim: number, shadow: number, kind: number, extra: number, fade: number): void {
    if (fade <= 0.001) return;
    if (this.n >= this.capacity) this.grow(this.capacity * 2);
    const i = this.n++;
    this.pos.setXYZW(i, x, y + 0.004, z, width);
    this.col.setXYZW(i, rgb.r, rgb.g, rgb.b, rim);
    this.prm.setXYZW(i, shadow, kind, extra, Math.min(1, fade));
  }

  end(): void {
    this.geo.instanceCount = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n === 0) return;
    dirty(this.pos, this.n); dirty(this.col, this.n); dirty(this.prm, this.n);
  }

  /** Frees the instanced geometry and the shader material this owns (the level calls it on dispose). */
  dispose(): void { this.geo.dispose(); (this.mesh.material as THREE.Material).dispose(); }

  private grow(capacity: number): void {
    const keep = this.n;
    const make = (): THREE.InstancedBufferAttribute => new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage);
    const pos = make(), col = make(), prm = make();
    if (this.capacity > 0) { pos.array.set(this.pos.array); col.array.set(this.col.array); prm.array.set(this.prm.array); this.geo.dispose(); }
    this.pos = pos; this.col = col; this.prm = prm;
    this.geo.setAttribute("aPos", pos);
    this.geo.setAttribute("aCol", col);
    this.geo.setAttribute("aPrm", prm);
    this.capacity = capacity;
    this.n = keep;
  }
}

/** Contact-shadow shrink for a unit hovering `height` above the ground (fliers). */
export function hoverShrink(height: number): number { return Math.max(0.35, 1 - height * 0.6); }
